'use client';
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef } from 'react';

import { PageNavigationEventContextType } from '../types';

export const PageNavigationEventContext = createContext<PageNavigationEventContextType>({
  registerNavigationEvent: (_: () => void) => {},
  removeNavigationEvent: (_: () => void) => {}
});

export const PageNavigationEventProvider = ({ children }: { children: ReactNode }) => {
  const actions = useRef<(() => void)[]>([]);
  const executeActions = useCallback(() => {
    actions.current.forEach((action) => {
      action();
    });
  }, []);

  const registerNavigationEvent = (action: () => void) => {
    actions.current.push(action);
  };

  const removeNavigationEvent = (action: () => void) => {
    actions.current = actions.current.filter((prevAction) => prevAction !== action);
  };

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        executeActions();
      }
    };
    const originPushState = window.history.pushState;
    const originReplaceState = window.history.replaceState;
    window.history.pushState = (data: unknown, unused: string, url?: string | URL | null) => {
      originPushState.apply(window.history, [data, unused, url]);
      executeActions();
    };
    window.history.replaceState = (data: unknown, unused: string, url?: string | URL | null) => {
      originReplaceState.apply(window.history, [data, unused, url]);
      executeActions();
    };
    window.addEventListener('popstate', executeActions);
    window.addEventListener('pageshow', handlePageShow); // Back/forward cache 대응

    return () => {
      window.history.pushState = originPushState;
      window.history.replaceState = originReplaceState;
      window.removeEventListener('popstate', executeActions);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [executeActions]);

  return <PageNavigationEventContext.Provider value={{ registerNavigationEvent, removeNavigationEvent }}>{children}</PageNavigationEventContext.Provider>;
};

export const usePageNavigationEvent = () => {
  return useContext(PageNavigationEventContext);
};
